import { RESERVATION_ANALYTICS_EVENT_TYPES, type ReservationAnalyticsEvent } from './analyticsTypes.ts';

const MAX_EVENT_BYTES = 8 * 1024;
const PII_KEYS = /(^|\.)(name|email|phone|phoneE164|comment|notes|contact|authUser|userId|ip|ipAddress)$/i;
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE = /(?:\+\d[\d\s().-]{7,}\d|\b\d{2,}[\s().-]\d[\d\s().-]{5,}\d\b)/;

const isValidTimezone = (value: unknown): value is string => {
  if (typeof value !== 'string' || !value.trim() || value.length > 100) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
};

export const validateReservationAnalyticsEvent = (event: ReservationAnalyticsEvent): string[] => {
  const errors: string[] = [];
  if (!event.eventId?.trim()) errors.push('missing eventId');
  if (!event.unitId?.trim()) errors.push('missing unitId');
  if (!event.timestamp || Number.isNaN(Date.parse(event.timestamp))) errors.push('missing timestamp');
  if (!event.sessionId?.trim() || !event.visitorId?.trim()) errors.push('invalid session');
  if (!isValidTimezone(event.timezone)) errors.push('invalid client timezone');
  if (event.unitTimezone !== undefined && !isValidTimezone(event.unitTimezone)) errors.push('invalid unit timezone');
  if (!RESERVATION_ANALYTICS_EVENT_TYPES.includes(event.eventType)) errors.push('unknown event type');
  if (event.schemaVersion !== 1) errors.push('unsupported schemaVersion');
  const serialized = JSON.stringify(event);
  if (new TextEncoder().encode(serialized).length > MAX_EVENT_BYTES) errors.push('payload too large');
  const scan = (value: unknown, path = '') => {
    if (PII_KEYS.test(path)) errors.push(`PII key rejected: ${path}`);
    if (typeof value === 'string' && !/(^|\.)(eventId|sessionId|visitorId)$/.test(path) && (EMAIL.test(value) || PHONE.test(value))) errors.push(`PII value rejected: ${path}`);
    if (value && typeof value === 'object') Object.entries(value as Record<string, unknown>).forEach(([key, child]) => scan(child, path ? `${path}.${key}` : key));
  };
  scan(event);
  return [...new Set(errors)];
};
